import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  type ReactNode,
} from "react";
import { Users } from "../types/Types";
import { setPaymentLogUserContext } from "../utils/paymentErrorLogger";

const STORAGE_KEY_OBJECT_ID = "objectId";

type AppContextType = {
  objectId: string | null;
  setObjectId: (id: string | null) => void;
  user: Users | undefined;
  setUser: React.Dispatch<React.SetStateAction<Users | undefined>>;
  sessionExpired: boolean;
  setSessionExpired: (expired: boolean) => void;
};

const AppContext = createContext<AppContextType | undefined>(undefined);
AppContext.displayName = "AppContext";

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const [objectId, setObjectId] = useState<string | null>(null);
  const [user, setUser] = useState<Users | undefined>(undefined);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Hydrate objectId from storage on mount only
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY_OBJECT_ID);
    if (stored) setObjectId(stored);
  }, []);

  // Persist objectId when it changes (setters are stable, no need in deps)
  useEffect(() => {
    if (objectId) {
      localStorage.setItem(STORAGE_KEY_OBJECT_ID, objectId);
    }
  }, [objectId]);

  // Sync current login user to payment error logger so ClientError logs save with user id/email/name (no N/A)
  useEffect(() => {
    const uid = objectId ?? user?.user?.objectId;
    const email = user?.user?.email;
    const name =
      user?.user?.firstName != null && user?.user?.lastName != null
        ? `${user.user.firstName} ${user.user.lastName}`.trim()
        : undefined;
    setPaymentLogUserContext({
      userId: uid ?? undefined,
      userEmail: email ?? undefined,
      userName: name || undefined,
    });
  }, [objectId, user]);

  const value = useMemo<AppContextType>(
    () => ({
      objectId,
      setObjectId,
      user,
      setUser,
      sessionExpired,
      setSessionExpired,
    }),
    [objectId, user, sessionExpired]
  );

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};
